// ─── Formatação monetária ────────────────────────────────────────
//
// formatCurrency(value, opts?) — BRL com 2 casas (default).
//   opts: { maximumFractionDigits, minimumFractionDigits }
// formatCurrencyCompact(value) — BRL sem decimais (atalho para
//   maximumFractionDigits=0).
// formatCurrencyShort(value)  — abreviado (R$ 250k, R$ 1,2M).
//   Útil em KPI cards onde o espaço é limitado.

export interface CurrencyOptions {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

export const formatCurrency = (
  value: number | string | null | undefined,
  options: CurrencyOptions = {},
): string => {
  if (value === null || value === undefined || value === '') return 'R$ 0';
  const num = typeof value === 'string' ? parseCurrency(value) : value;
  const {
    maximumFractionDigits = 0,
    minimumFractionDigits = 0,
  } = options;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits,
    minimumFractionDigits,
  }).format(num);
};

export const formatCurrencyCompact = (value: number | string | null | undefined): string =>
  formatCurrency(value, { maximumFractionDigits: 0, minimumFractionDigits: 0 });

export const formatCurrencyShort = (value: number | string | null | undefined): string => {
  const v = Number(value) || 0;
  if (v >= 1_000_000) return `R$ ${Math.round(v / 1_000_000)}M`;
  if (v >= 1_000)     return `R$ ${Math.round(v / 1_000)}k`;
  return formatCurrencyCompact(v);
};

// ─── Porcentagem ─────────────────────────────────────────────────
//
// formatPercent(value, opts?) — recebe valor em proporção (0..1).
//   opts: { digits, fallback }
// Retorna '<n,nn>%' com vírgula como separador. fallback exibido
// quando value é null/undefined/NaN.
export interface PercentOptions {
  digits?: number;
  fallback?: string;
}

export const formatPercent = (
  value: number | string | null | undefined,
  options: PercentOptions = {},
): string => {
  const { digits = 1, fallback = '—' } = options;
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return fallback;
  }
  return `${(Number(value) * 100).toFixed(digits).replace('.', ',')}%`;
};

export const parseCurrency = (str: unknown): number => {
  if (!str) return 0;
  const cleaned = String(str).replace(/R\$\s*/g, '').replace(/\./g, '').replace(',', '.').trim();
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

// parseCurrency só entende texto em pt-BR, onde o ponto é MILHAR. Passar um
// número cru para ela corrompe o valor: 39.9 vira "39.9", perde o ponto e sai
// 399 — dez vezes o preço. Isso nunca apareceu enquanto o catálogo era só de
// reais inteiros; com centavos, aparece em toda linha.
// Esta versão aceita as duas origens: número passa direto, texto vai para o
// parser pt-BR.
export const parseMoney = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  return parseCurrency(value);
};

// Valor para dentro de <input type="text">: sem símbolo de moeda repetido no
// meio da digitação, mas com os centavos preservados. String vazia quando zero,
// para o campo poder ficar em branco.
export const formatMoneyInput = (value: unknown): string => {
  const n = parseMoney(value);
  if (!n) return '';
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// formatRange(min, max) — formata um intervalo monetário como
// "Entre R$ X e R$ Y", "Até R$ Y" ou "A partir de R$ X" conforme
// quais valores existem. Retorna '' se nenhum.
export const formatRange = (
  min: number | string | null | undefined,
  max: number | string | null | undefined,
): string => {
  const fmt = (v: number | string | null | undefined) => formatCurrencyInteger(v);
  const hasMin = min != null && min !== '' && Number(min) > 0;
  const hasMax = max != null && max !== '' && Number(max) > 0;
  if (hasMin && hasMax) return `Entre ${fmt(min)} e ${fmt(max)}`;
  if (hasMin) return `A partir de ${fmt(min)}`;
  if (hasMax) return `Até ${fmt(max)}`;
  return '';
};

// parseRangeFromText(text) — detecta se um texto é um intervalo
// salvo via formatRange e devolve { min, max } numéricos. Retorna
// null se o texto não casar com o padrão.
export const parseRangeFromText = (
  text: string | null | undefined,
): { min: number | null; max: number | null } | null => {
  if (!text) return null;
  const s = String(text);
  const entre = s.match(/entre\s+R\$\s*([\d.,]+)\s+e\s+R\$\s*([\d.,]+)/i);
  if (entre) return { min: parseCurrency(entre[1]), max: parseCurrency(entre[2]) };
  const partir = s.match(/a\s*partir\s*de\s+R\$\s*([\d.,]+)/i);
  if (partir) return { min: parseCurrency(partir[1]), max: null };
  const ate = s.match(/at[ée]\s+R\$\s*([\d.,]+)/i);
  if (ate) return { min: null, max: parseCurrency(ate[1]) };
  return null;
};

export const formatIntegerBR = (value: unknown): string => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

export const formatCurrencyInteger = (value: number | string | null | undefined): string => {
  const num = typeof value === 'string' ? parseCurrency(value) : Number(value);
  if (!Number.isFinite(num) || num === 0) return 'R$ 0';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Math.round(num));
};

export const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return '';
  let cleaned = phone.replace(/\D/g, '');

  // Remove DDI 55 caso ja venha incluido
  if ((cleaned.length === 12 || cleaned.length === 13) && cleaned.startsWith('55')) {
    cleaned = cleaned.slice(2);
  }

  if (cleaned.length === 11) {
    return `+55 (${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  } else if (cleaned.length === 10) {
    return `+55 (${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }

  return phone;
};

export const formatCPF = (cpf: string | null | undefined): string => {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');

  if (cleaned.length === 11) {
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9)}`;
  }

  return cpf;
};

export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};
